import {
  ListSchedule,
  CalendarEvent,
  CalendarViewOptions,
  TodaysPriority,
  WeekViewData,
  DayViewData,
  CalendarStats,
  RecurringPattern,
  WorkingHours,
  DEFAULT_WORKING_HOURS,
  DayOfWeek
} from '../types/calendar';
import { List } from '../types/lists';
import { Goal } from '../types/goals';
import { Aspiration } from '../types/aspirations';
import { personaService } from './personaService';
import { universalCompletionService } from './universalCompletionService';
import { devDateTimeService } from './devDateTimeService';

const SCHEDULES_STORAGE_KEY = 'godmode_list_schedules';

export class CalendarService {
  private schedules: ListSchedule[] = [];
  private workingHours: WorkingHours = DEFAULT_WORKING_HOURS;

  constructor() {
    this.loadSchedules();
  }

  // Load schedules from localStorage
  private loadSchedules(): void {
    try {
      // Ensure persona data is loaded
      personaService.initializeIfNeeded();
      
      const storedSchedules = localStorage.getItem(SCHEDULES_STORAGE_KEY);
      if (storedSchedules) {
        this.schedules = JSON.parse(storedSchedules).map((schedule: any) => ({
          ...schedule,
          scheduledDate: schedule.scheduledDate ? new Date(schedule.scheduledDate) : undefined,
          createdAt: new Date(schedule.createdAt),
          updatedAt: new Date(schedule.updatedAt),
          recurringPattern: schedule.recurringPattern ? {
            ...schedule.recurringPattern,
            startDate: new Date(schedule.recurringPattern.startDate),
            endDate: schedule.recurringPattern.endDate ? new Date(schedule.recurringPattern.endDate) : undefined,
            customDates: schedule.recurringPattern.customDates?.map((date: string) => new Date(date))
          } : undefined
        }));
      } else {
        // No longer initializing sample schedules - data comes from persona
        console.log('📊 No schedules found, data should be loaded from persona');
        this.schedules = [];
      }
    } catch (error) {
      console.error('Error loading schedules:', error);
      this.schedules = [];
    }
  }

  // Save schedules to localStorage
  private saveSchedules(): void {
    try {
      localStorage.setItem(SCHEDULES_STORAGE_KEY, JSON.stringify(this.schedules));
    } catch (error) {
      console.error('Error saving schedules:', error);
    }
  }


  // Create a new schedule
  createSchedule(scheduleData: Omit<ListSchedule, 'id' | 'createdAt' | 'updatedAt'>): ListSchedule {
    const now = new Date();
    const schedule: ListSchedule = {
      ...scheduleData,
      id: `schedule-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      createdAt: now,
      updatedAt: now
    };
    
    this.schedules.push(schedule);
    this.saveSchedules();
    return schedule;
  }

  // Update an existing schedule
  updateSchedule(scheduleId: string, updates: Partial<ListSchedule>): ListSchedule {
    const index = this.schedules.findIndex(s => s.id === scheduleId);
    if (index === -1) {
      throw new Error(`Schedule with id ${scheduleId} not found`);
    }

    this.schedules[index] = {
      ...this.schedules[index],
      ...updates,
      updatedAt: new Date()
    };

    this.saveSchedules();
    return this.schedules[index];
  }

  // Delete a schedule
  deleteSchedule(scheduleId: string): void {
    this.schedules = this.schedules.filter(s => s.id !== scheduleId);
    this.saveSchedules();
  }

  // Get all schedules
  getAllSchedules(): ListSchedule[] {
    return [...this.schedules];
  }

  // Get schedules for a specific list
  getSchedulesForList(listId: string): ListSchedule[] {
    return this.schedules.filter(s => s.listId === listId && s.isActive);
  }

  // Generate calendar events for a date range
  generateCalendarEvents(
    startDate: Date, 
    endDate: Date, 
    lists: List[],
    goals?: Goal[],
    aspirations?: Aspiration[]
  ): CalendarEvent[] {
    const events: CalendarEvent[] = [];
    const listsMap = new Map(lists.map(list => [list.id, list]));
    const goalsMap = new Map(goals?.map(goal => [goal.id, goal]) || []);
    const aspirationsMap = new Map(aspirations?.map(aspiration => [aspiration.id, aspiration]) || []);

    for (const schedule of this.schedules.filter(s => s.isActive)) {
      const list = listsMap.get(schedule.listId);
      if (!list) continue;

      const goal = list.goalId ? goalsMap.get(list.goalId) : undefined;
      const aspiration = goal ? aspirationsMap.get(goal.aspirationId) : undefined;

      if (schedule.type === 'one_time' && schedule.scheduledDate) {
        if (schedule.scheduledDate >= startDate && schedule.scheduledDate <= endDate) {
          events.push(this.createCalendarEvent(schedule, list, schedule.scheduledDate, goal, aspiration));
        }
      } else if (schedule.recurringPattern) {
        const recurringEvents = this.generateRecurringEvents(
          schedule, 
          list, 
          startDate, 
          endDate,
          goal,
          aspiration
        );
        events.push(...recurringEvents);
      }
    }

    // Sort events by start time
    events.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
    return events;
  }

  // Generate recurring events within date range
  private generateRecurringEvents(
    schedule: ListSchedule,
    list: List,
    startDate: Date,
    endDate: Date,
    goal?: Goal,
    aspiration?: Aspiration
  ): CalendarEvent[] {
    const events: CalendarEvent[] = [];
    const pattern = schedule.recurringPattern!;
    
    let currentDate = new Date(Math.max(pattern.startDate.getTime(), startDate.getTime()));
    const rangeEnd = new Date(Math.min(
      pattern.endDate?.getTime() || endDate.getTime(),
      endDate.getTime()
    ));

    let occurrenceCount = 0;
    const maxOccurrences = pattern.maxOccurrences || 1000; // Reasonable limit

    while (currentDate <= rangeEnd && occurrenceCount < maxOccurrences) {
      let shouldCreateEvent = false;

      switch (schedule.type) {
        case 'daily':
          shouldCreateEvent = this.shouldCreateDailyEvent(pattern, currentDate);
          break;
        case 'weekly':
          shouldCreateEvent = this.shouldCreateWeeklyEvent(pattern, currentDate);
          break;
        case 'monthly':
          shouldCreateEvent = this.shouldCreateMonthlyEvent(pattern, currentDate);
          break;
        case 'custom':
          shouldCreateEvent = this.shouldCreateCustomEvent(pattern, currentDate);
          break;
      }

      if (shouldCreateEvent && currentDate >= startDate) {
        const eventDate = new Date(currentDate);
        const [hours, minutes] = pattern.time.split(':').map(Number);
        eventDate.setHours(hours, minutes, 0, 0);
        
        events.push(this.createCalendarEvent(schedule, list, eventDate, goal, aspiration, `${schedule.id}-${currentDate.toISOString().split('T')[0]}`));
        occurrenceCount++;
      }

      // Move to next day
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return events;
  }

  // Check if daily event should be created
  private shouldCreateDailyEvent(pattern: RecurringPattern, date: Date): boolean {
    const daysSinceStart = Math.floor(
      (date.getTime() - pattern.startDate.getTime()) / (24 * 60 * 60 * 1000)
    );
    
    if (pattern.dailyInterval && daysSinceStart % pattern.dailyInterval !== 0) {
      return false;
    }

    if (pattern.daysOfWeek) {
      const dayOfWeek = this.getDayOfWeekString(date);
      return pattern.daysOfWeek.includes(dayOfWeek);
    }

    return true;
  }

  // Check if weekly event should be created
  private shouldCreateWeeklyEvent(pattern: RecurringPattern, date: Date): boolean {
    if (!pattern.daysOfWeek) return false;

    const dayOfWeek = this.getDayOfWeekString(date);
    if (!pattern.daysOfWeek.includes(dayOfWeek)) return false;

    if (pattern.weeklyInterval && pattern.weeklyInterval > 1) {
      const weeksSinceStart = Math.floor(
        (date.getTime() - pattern.startDate.getTime()) / (7 * 24 * 60 * 60 * 1000)
      );
      return weeksSinceStart % pattern.weeklyInterval === 0;
    }

    return true;
  }

  // Check if monthly event should be created
  private shouldCreateMonthlyEvent(pattern: RecurringPattern, date: Date): boolean {
    if (pattern.dayOfMonth) {
      return date.getDate() === pattern.dayOfMonth;
    }

    if (pattern.weekOfMonth && pattern.dayOfWeekInMonth) {
      const dayOfWeek = this.getDayOfWeekString(date);
      if (dayOfWeek !== pattern.dayOfWeekInMonth) return false;

      const weekOfMonth = this.getWeekOfMonth(date);
      return weekOfMonth === pattern.weekOfMonth;
    }

    return false;
  }

  // Check if custom event should be created
  private shouldCreateCustomEvent(pattern: RecurringPattern, date: Date): boolean {
    if (!pattern.customDates) return false;
    
    const dateString = date.toISOString().split('T')[0];
    return pattern.customDates.some(customDate => 
      customDate.toISOString().split('T')[0] === dateString
    );
  }

  // Get day of week as string
  private getDayOfWeekString(date: Date): DayOfWeek {
    const days: DayOfWeek[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days[date.getDay()];
  }

  // Get week of month (1-4, or -1 for last week)
  private getWeekOfMonth(date: Date): number {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    
    // If it's the last occurrence of this day of week in the month
    const nextWeek = new Date(date);
    nextWeek.setDate(date.getDate() + 7);
    if (nextWeek.getMonth() !== date.getMonth()) {
      return -1; // Last week
    }

    return Math.ceil((date.getDate() + firstDay.getDay()) / 7);
  }

  // Create a calendar event from schedule and list
  private createCalendarEvent(
    schedule: ListSchedule,
    list: List,
    eventDate: Date,
    goal?: Goal,
    aspiration?: Aspiration,
    recurringInstanceId?: string
  ): CalendarEvent {
    const endTime = new Date(eventDate);
    endTime.setMinutes(endTime.getMinutes() + (list.estimatedHours || 1) * 60);

    return {
      id: recurringInstanceId || `${schedule.id}-${eventDate.toISOString()}`,
      listId: list.id,
      scheduleId: schedule.id,
      title: schedule.title || list.title,
      description: schedule.description || list.description,
      startTime: eventDate,
      endTime: endTime,
      isAllDay: false,
      
      // List information
      listTitle: list.title,
      listStatus: list.status,
      listProgress: this.getListProgressForDate(list, eventDate),
      goalId: goal?.id,
      goalTitle: goal?.title,
      aspirationId: aspiration?.id,
      aspirationTitle: aspiration?.title,
      
      // Event metadata
      isRecurring: schedule.type !== 'one_time',
      recurringInstanceId,
      color: list.color || this.getDefaultColorForStatus(list.status),
      priority: list.priority
    };
  }

  // Get default color for list status
  private getDefaultColorForStatus(status: string): string {
    switch (status) {
      case 'template': return '#8B5CF6'; // Purple
      case 'ready': return '#10B981'; // Green
      case 'in_progress': return '#F59E0B'; // Orange
      case 'complete': return '#6B7280'; // Gray
      default: return '#3B82F6'; // Blue
    }
  }

  // Get week view data
  getWeekViewData(
    startDate: Date,
    lists: List[],
    goals?: Goal[],
    aspirations?: Aspiration[],
    _options?: CalendarViewOptions
  ): WeekViewData {
    // For rolling 7-day view, use the provided startDate directly
    const weekStart = new Date(startDate);
    weekStart.setHours(0, 0, 0, 0);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);

    const events = this.generateCalendarEvents(weekStart, weekEnd, lists, goals, aspirations);
    const days: DayViewData[] = [];

    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(date.getDate() + i);
      
      const dayEvents = events.filter(event => 
        event.startTime.toDateString() === date.toDateString()
      );

      days.push({
        date,
        isToday: date.toDateString() === devDateTimeService.now().toDateString(),
        isWeekend: date.getDay() === 0 || date.getDay() === 6,
        events: dayEvents,
        priorities: this.getTodaysPriorities(date, lists, dayEvents),
        totalScheduledMinutes: dayEvents.reduce((total, event) => {
          const duration = event.endTime ? 
            (event.endTime.getTime() - event.startTime.getTime()) / (1000 * 60) : 60;
          return total + duration;
        }, 0),
        availableMinutes: this.getAvailableMinutes(date)
      });
    }

    return {
      weekStart,
      weekEnd,
      days
    };
  }



  // Get today's priorities
  getTodaysPriorities(date: Date, lists: List[], events?: CalendarEvent[]): TodaysPriority[] {
    const today = new Date(date);
    today.setHours(0, 0, 0, 0);
    
    const priorities: TodaysPriority[] = [];
    const scheduledListIds = new Set(events?.map(e => e.listId) || []);

    // Add scheduled lists
    for (const event of events || []) {
      const list = lists.find(l => l.id === event.listId);
      if (!list) continue;

      priorities.push({
        id: `priority-${event.id}`,
        listId: list.id,
        title: list.title,
        description: list.description,
        priority: list.priority,
        progress: this.getListProgressForDate(list, event.startTime),
        totalItems: this.getListTotalItemsForDate(list, event.startTime),
        completedItems: this.getListCompletedItemsForDate(list, event.startTime),
        scheduledTime: event.startTime.toTimeString().slice(0, 5),
        isOverdue: list.deadline ? list.deadline < devDateTimeService.now() && list.status !== 'complete' : false,
        goalTitle: event.goalTitle,
        aspirationTitle: event.aspirationTitle,
        estimatedMinutes: list.estimatedHours ? list.estimatedHours * 60 : undefined
      });
    }

    // Add high-priority unscheduled lists with deadlines
    for (const list of lists) {
      if (scheduledListIds.has(list.id)) continue;
      if (list.status === 'complete') continue;
      
      const hasDeadlineToday = list.deadline && 
        list.deadline.toDateString() === today.toDateString();
      const isHighPriority = list.priority <= 3;
      
      if (hasDeadlineToday || isHighPriority) {
        priorities.push({
          id: `priority-unscheduled-${list.id}`,
          listId: list.id,
          title: list.title,
          description: list.description,
          priority: list.priority,
          progress: this.getListProgressForDate(list, today),
          totalItems: this.getListTotalItemsForDate(list, today),
          completedItems: this.getListCompletedItemsForDate(list, today),
          isOverdue: list.deadline ? list.deadline < new Date() : false,
          estimatedMinutes: list.estimatedHours ? list.estimatedHours * 60 : undefined
        });
      }
    }

    // Sort by priority (lower number = higher priority)
    priorities.sort((a, b) => {
      if (a.isOverdue && !b.isOverdue) return -1;
      if (!a.isOverdue && b.isOverdue) return 1;
      return a.priority - b.priority;
    });

    return priorities;
  }

  // Get available minutes for a day based on working hours
  private getAvailableMinutes(date: Date): number {
    const dayOfWeek = this.getDayOfWeekString(date);
    const workingHours = this.workingHours[dayOfWeek];
    
    if (!workingHours.isWorkingDay) return 0;
    
    const [startHour, startMinute] = workingHours.startTime.split(':').map(Number);
    const [endHour, endMinute] = workingHours.endTime.split(':').map(Number);
    
    const totalMinutes = (endHour * 60 + endMinute) - (startHour * 60 + startMinute);
    
    // Subtract break times
    const breakMinutes = workingHours.breakTimes?.reduce((total, breakTime) => {
      const [breakStartHour, breakStartMinute] = breakTime.start.split(':').map(Number);
      const [breakEndHour, breakEndMinute] = breakTime.end.split(':').map(Number);
      return total + ((breakEndHour * 60 + breakEndMinute) - (breakStartHour * 60 + breakStartMinute));
    }, 0) || 0;
    
    return Math.max(0, totalMinutes - breakMinutes);
  }

  // Get calendar statistics
  getCalendarStats(
    lists: List[],
    startDate: Date = devDateTimeService.now(),
    endDate: Date = new Date(devDateTimeService.nowTimestamp() + 7 * 24 * 60 * 60 * 1000)
  ): CalendarStats {
    const events = this.generateCalendarEvents(startDate, endDate, lists);
    const scheduledListIds = new Set(events.map(e => e.listId));
    const now = devDateTimeService.now();

    const completedThisWeek = lists.filter(list =>
      list.status === 'complete' &&
      list.completedDate &&
      list.completedDate >= startDate &&
      list.completedDate <= endDate
    ).length;

    const overdueCount = lists.filter(list =>
      list.deadline &&
      list.deadline < now &&
      list.status !== 'complete'
    ).length;

    const upcomingDeadlines = {
      today: lists.filter(list =>
        list.deadline &&
        list.deadline.toDateString() === now.toDateString() &&
        list.status !== 'complete'
      ).length,
      thisWeek: lists.filter(list =>
        list.deadline &&
        list.deadline >= now &&
        list.deadline <= new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000) &&
        list.status !== 'complete'
      ).length,
      nextWeek: lists.filter(list =>
        list.deadline &&
        list.deadline >= new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000) &&
        list.deadline <= new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000) &&
        list.status !== 'complete'
      ).length
    };

    const completedLists = lists.filter(list => list.status === 'complete');
    const averageCompletionRate = lists.length > 0 ? 
      (completedLists.length / lists.length) * 100 : 0;

    // Calculate most productive day (simplified)
    const dayStats = new Map<DayOfWeek, number>();
    completedLists.forEach(list => {
      if (list.completedDate) {
        const day = this.getDayOfWeekString(list.completedDate);
        dayStats.set(day, (dayStats.get(day) || 0) + 1);
      }
    });

    let mostProductiveDay: DayOfWeek = 'monday';
    let maxCompletions = 0;
    dayStats.forEach((count, day) => {
      if (count > maxCompletions) {
        maxCompletions = count;
        mostProductiveDay = day;
      }
    });

    return {
      totalScheduledLists: scheduledListIds.size,
      completedThisWeek,
      overdueCount,
      upcomingDeadlines,
      averageCompletionRate,
      mostProductiveDay
    };
  }

  // Set working hours
  setWorkingHours(workingHours: WorkingHours): void {
    this.workingHours = workingHours;
    // Save to localStorage if needed
    localStorage.setItem('godmode_working_hours', JSON.stringify(workingHours));
  }

  // Get working hours
  getWorkingHours(): WorkingHours {
    return { ...this.workingHours };
  }

  // Helper methods for contextual completion data
  private getListProgressForDate(list: List, date: Date): number {
    const listData = universalCompletionService.getListDataForContext(list, date);
    const progress = listData ? listData.progress : 0;
    console.log(`📊 CalendarService: Progress for ${list.title} on ${date.toDateString()}: ${progress}% (${listData?.completedItems}/${listData?.totalItems})`);
    return progress;
  }

  private getListTotalItemsForDate(list: List, date: Date): number {
    const listData = universalCompletionService.getListDataForContext(list, date);
    return listData ? listData.totalItems : list.items.length;
  }

  private getListCompletedItemsForDate(list: List, date: Date): number {
    const listData = universalCompletionService.getListDataForContext(list, date);
    return listData ? listData.completedItems : 0;
  }
}

// Create singleton instance
export const calendarService = new CalendarService();